import { GET } from "../../components/CustomComponent.js";
import ICON from "./icons.js";

/* ============================================================================
 * ตัวเชื่อม API ของเว็บฝั่งลูกค้า (เปิดสาธารณะ ไม่ต้องล็อกอิน)
 *
 *   GET home/content            -> เนื้อหาหน้าแรกทั้งหน้า + ทรัพย์ + โครงการ
 *   GET home/properties         -> รายการทรัพย์ (ใช้ตอนกรอง/ดูเพิ่ม)
 *   GET home/properties/:uid    -> รายละเอียดทรัพย์ + ทรัพย์ใกล้เคียง
 *
 * ทุกฟังก์ชันคืนค่าที่ render ได้เสมอ ถึง API จะล่ม — หน้าแรกจะขึ้นโครงว่าง
 * แทนที่จะพังทั้งหน้า (บริการเช็ก error ของ GET เด้ง alert ให้อยู่แล้ว)
 * ========================================================================== */

/* โครงเปล่าที่ใช้ระหว่างรอ API ตอบ — ทุกคอมโพเนนต์จึง render ได้โดยไม่ต้องเช็ก null */
export const EMPTY_CONTENT = {
    hero: { setting: { autoplay: true, interval: 6 }, slides: [] },
    categories: [],
    banners: [],
    lifestyles: { section: { title: "" }, items: [] },
    transit: { section: { title: "" }, items: [] },
    features: { section: { title: "" }, items: [], stats: [] },
    articles: { section: { title: "" }, items: [] },
    contact: {
        bar: { title: "", desc: "", cta: "home_cta_contact", items: [] },
        footer: { tagline: "", newsletter_title: "", newsletter_desc: "", company: "SafeSpace Life Living" },
        socials: [],
    },
    properties: [],
    projects: [],
    zones: [],
    types: [],
    project_names: [],
};

/* ไอคอนของช่องทางติดต่อเป็นเรื่องการแสดงผล จึงเก็บไว้ฝั่งหน้าเว็บ
   ไม่ให้ API ส่งมาด้วย — key ต้องตรงกับ kind ที่ backend ส่ง */
export const CONTACT_ICONS = {
  phone: ICON.phone,
  line: ICON.comment,
  mail: ICON.mail,
  map: ICON.marker,
  clock: ICON.clock,
};

/** เนื้อหาหน้าแรกทั้งหน้าในครั้งเดียว — หน้าแรกต้องขึ้นครบพร้อมกัน ไม่ทยอยโผล่ */
export const loadHomeContent = async () => {
  const result = await GET("home/content", {});
  if (!result?.status || !result.data) return EMPTY_CONTENT;

  // เติมคีย์ที่ขาดด้วยโครงเปล่า เผื่อ API เวอร์ชันเก่ายังไม่ส่งบางส่วนมา
  return { ...EMPTY_CONTENT, ...result.data };
};

/**
 * รายการทรัพย์ตามเงื่อนไข — ใช้ในหน้ารายการทรัพย์ (กรอง / เรียง / แบ่งหน้า)
 * คืน total มาด้วย หน้ารายการจะได้บอกจำนวนผลลัพธ์และรู้ว่ายังมีหน้าถัดไปไหม
 */
export const loadProperties = async (params = {}) => {
  const result = await GET("home/properties", params);
  const data = result?.status ? result.data : null;
  return { items: data?.items || [], total: Number(data?.total) || 0 };
};

/* API จำกัดจำนวนต่อครั้งไว้ที่ 48 (MAX_LIMIT ฝั่ง home.service) — ถ้าผู้ใช้บันทึก
   ไว้มากกว่านั้นก็แบ่งยิงเป็นชุด แล้วรวมผลกลับมาเป็นรายการเดียว */
const FAVORITE_CHUNK = 48;

/**
 * ข้อมูลจริงของทรัพย์ที่ผู้ใช้บันทึกไว้ (หน้ารายการโปรด)
 *
 * ส่ง uid ที่เก็บไว้ที่เครื่องขึ้นไปขอเป็นชุด — ไม่ไล่ยิงรายละเอียดทีละใบ เพราะ
 * หน้านี้ต้องการแค่ข้อมูลระดับการ์ด และรายละเอียดใบหนึ่งพ่วงทรัพย์ใกล้เคียงมาด้วย
 *
 * ผลลัพธ์เรียงตามลำดับที่ผู้ใช้กดบันทึก (ล่าสุดอยู่บน) ไม่ใช่ลำดับของ API
 * และคืนเฉพาะรายการที่ยังเผยแพร่อยู่ — uid ที่หายไปคือประกาศที่ถูกปิด/ลบแล้ว
 * หน้าเรียกใช้จะได้เอาไปล้างออกจากเครื่องต่อ (ดู missing)
 */
export const loadFavoriteProperties = async (uids = []) => {
  if (!uids.length) return { items: [], missing: [] };

  const chunks = [];
  for (let i = 0; i < uids.length; i += FAVORITE_CHUNK) chunks.push(uids.slice(i, i + FAVORITE_CHUNK));

  const results = await Promise.all(chunks.map((chunk) => loadProperties({ uids: chunk.join(","), limit: FAVORITE_CHUNK })));

  const order = new Map(uids.map((uid, index) => [uid, index]));
  const items = results
    .flatMap((result) => result.items)
    .sort((a, b) => (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0));

  const found = new Set(items.map((item) => item.id));
  return { items, missing: uids.filter((uid) => !found.has(uid)) };
};

/** บทความทั้งหมด — หน้า "บทความอสังหาฯ" แบบเต็ม */
export const loadArticles = async () => {
  const result = await GET("home/articles", {});
  const data = result?.status ? result.data : null;
  return { section: data?.section || { title: "" }, items: data?.items || [] };
};

/** รายละเอียดทรัพย์ + ทรัพย์ใกล้เคียง (มาพร้อมกันใน response เดียว) */
export const loadProperty = async (uid) => {
  const result = await GET(`home/properties/${uid}`, {});
  return result?.status && result.data ? result.data : null;
};

/**
 * ส่งคำขอ "ลงทะเบียนสนใจทรัพย์" — ลงตาราง customers ชุดเดียวกับปุ่มเพิ่มลูกค้าของแอดมิน
 *
 * เรียก fetch ตรงแทน POST() ของ services/api ด้วยเหตุผลเดียวกับ trackPropertyView :
 * POST() เด้ง Swal ภาษาอังกฤษ ("Warning") ทุกครั้งที่ API ตอบไม่สำเร็จ ซึ่งจะไปซ้อนกับ
 * ข้อความของหน้านี้ที่ต้องเป็นภาษาตามที่ผู้ใช้เลือก อีกทั้ง POST() ตัดคีย์ที่เป็นค่าว่างทิ้ง
 * ก่อนส่ง ทำให้ช่องที่ตั้งใจไม่กรอกกับช่องที่ตั้งใจล้างค่าแยกกันไม่ออก
 *
 * คืน { status, code } — code คือรหัสลูกค้าที่ระบบออกให้ (LD-YYMM-0001) เอาไว้ให้ผู้ส่ง
 * อ้างอิงตอนผู้ดูแลโทรกลับ
 */
export const submitPropertyLead = async (uid, payload) => {
  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}home/properties/${uid}/lead`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const result = await response.json();

    return { status: !!result?.status, code: result?.data?.code || "" };
  } catch (error) {
    // เน็ตหลุด / ยิงไม่ถึงเซิร์ฟเวอร์ — หน้าเรียกใช้เป็นคนแจ้งผู้ใช้เอง
    return { status: false, code: "" };
  }
};

/**
 * บันทึกว่ามีคนเปิดดูประกาศนี้ — ตัวเลขที่ไปโผล่ในแดชบอร์ดหลังบ้าน
 * (การ์ดยอดเข้าชม / 10 ทรัพย์ที่มีคนดูสูงสุด) และหน้ารายงานทรัพย์
 *
 * ยิงทิ้งไว้เฉย ๆ ไม่รอผลและไม่แจ้งผู้ชมไม่ว่าจะสำเร็จหรือไม่ จึงเรียก fetch ตรง
 * แทน POST() ของ services/api ที่เด้ง Swal ทุกครั้งที่ API ตอบไม่สำเร็จ — ผู้ชม
 * ไม่ควรเจอป๊อปอัปเรื่องการเก็บสถิติที่ไม่เกี่ยวกับสิ่งที่เขากำลังทำอยู่
 *
 * ฝั่ง backend กันนับซ้ำให้แล้ว (คนเดิม + ทรัพย์เดิมภายใน 30 นาที นับครั้งเดียว)
 * หน้าเว็บจึงเรียกได้ทุกครั้งที่เปิดหน้าโดยไม่ต้องจำเองว่าเคยนับไปหรือยัง
 */
export const trackPropertyView = (uid) => {
  if (!uid) return;

  try {
    fetch(`${process.env.REACT_APP_API_URL}home/properties/${uid}/view`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      // ยังส่งให้ครบแม้ผู้ชมกดปิดแท็บทันทีหลังหน้าโหลดเสร็จ
      keepalive: true,
      body: JSON.stringify({ referrer: document.referrer || "" }),
    }).catch(() => {});
  } catch (e) {}
};
